import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useMyCardsContext } from '#src/contexts/myCards/index.tsx';

const Root = styled.div`
  width: 100%;
  @media ${laptopAndAbove} {
    width: unset;
  }
`;

const Item = styled(ButtonBase)`
  padding: var(--double-unit) var(--quadruple-unit);
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--double-unit);
  opacity: 0.3;
  transition: opacity 0.2s ease-in-out;
  border: 1px solid transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &:hover,
  &:focus {
    opacity: 0.7;
  }
  &.active {
    opacity: 1;
  }
  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit);
  }
`;

const Icon = styled.img`
  object-fit: contain;
  height: var(--double-unit);
`;

type Props = {
  cardSetSlug: string;
  path: (cardSetSlug: string) => string;
  className?: string;
};

export const CollectionSetSwitcher = ({
  cardSetSlug,
  path,
  className,
}: Props) => {
  const getCardSetConfig = useGetCardSetConfig();
  const { cardSets } = useMyCardsContext();
  const isLaptop = useIsLaptop();
  if (cardSets.length <= 1) {
    return null;
  }
  return (
    <Root className={className}>
      <Scrollable
        itemToDisplay={isLaptop ? cardSets.length : 2}
        itemGap={1}
        overhang={0}
      >
        {cardSets
          .map(cardSet => {
            const currentCardSetSlug = cardSet.slug;
            const { visual, colors, background } = getCardSetConfig(
              cardSet.slug
            );
            return (
              <Item
                key={currentCardSetSlug}
                to={path(currentCardSetSlug)}
                replace
                className={classNames({
                  active: currentCardSetSlug === cardSetSlug,
                })}
                style={{
                  ...background,
                  borderColor: colors.main,
                }}
              >
                {visual.brand.logoImage ? (
                  <Icon src={visual.brand.logoImage} alt="" />
                ) : (
                  <BrandXS className="text-center">{cardSet.name}</BrandXS>
                )}
              </Item>
            );
          })
          .filter(Boolean)}
      </Scrollable>
    </Root>
  );
};
